import React, { useRef, useEffect } from "react";

const POINTS = 30;
const LINE_DIST_DARK = 360;
const LINE_DIST_LIGHT = 280;
const SPEED = 0.13;
const COLOR_DARK = "#7ecfff";
const LINE_COLOR_DARK = "rgba(126,207,255,0.13)";
const PACKET_COLOR_DARK = "#fff";
const COLOR_LIGHT = "#222";
const LINE_COLOR_LIGHT = "rgba(0,0,0,0.13)";
const PACKET_COLOR_LIGHT = "#222";
const PACKET_RADIUS = 2.2;
const PACKETS = 2;

function random(min, max) {
  return Math.random() * (max - min) + min;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

const NetworkBackground = () => {
  const canvasRef = useRef(null);
  const points = useRef([]);
  const lines = useRef([]);
  const packets = useRef([]);
  const animationRef = useRef();

  // Определяем тему
  const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const LINE_DIST = isDark ? LINE_DIST_DARK : LINE_DIST_LIGHT;
  const COLOR = isDark ? COLOR_DARK : COLOR_LIGHT;
  const LINE_COLOR = isDark ? LINE_COLOR_DARK : LINE_COLOR_LIGHT;
  const PACKET_COLOR = isDark ? PACKET_COLOR_DARK : PACKET_COLOR_LIGHT;
  const PACKET_SHADOW = isDark ? COLOR_DARK : COLOR_LIGHT;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    // Инициализация точек (теперь только в центральной области)
    const xMin = width * 0.2;
    const xMax = width * 0.8;
    const yMin = height * 0.2;
    const yMax = height * 0.8;
    points.current = Array.from({ length: POINTS }, () => ({
      x: random(xMin, xMax),
      y: random(yMin, yMax),
      vx: random(-SPEED, SPEED),
      vy: random(-SPEED, SPEED)
    }));

    // Генерация линий между точками
    function updateLines() {
      lines.current = [];
      for (let i = 0; i < POINTS; i++) {
        for (let j = i + 1; j < POINTS; j++) {
          const a = points.current[i];
          const b = points.current[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINE_DIST) {
            lines.current.push({ a, b, dist });
          }
        }
      }
    }
    updateLines();

    // Инициализация пакетов
    function spawnPacket() {
      if (lines.current.length === 0) return;
      const line = lines.current[Math.floor(random(0, lines.current.length))];
      const dir = Math.random() > 0.5 ? 1 : -1;
      packets.current.push({
        a: line.a,
        b: line.b,
        t: dir === 1 ? 0 : 1,
        dir,
        speed: random(0.004, 0.009)
      });
    }
    packets.current = [];
    for (let i = 0; i < PACKETS; i++) spawnPacket();

    function animate() {
      ctx.clearRect(0, 0, width, height);
      // Линии
      ctx.save();
      for (const line of lines.current) {
        ctx.strokeStyle = LINE_COLOR;
        ctx.lineWidth = 1.1 - line.dist / LINE_DIST;
        ctx.beginPath();
        ctx.moveTo(line.a.x, line.a.y);
        ctx.lineTo(line.b.x, line.b.y);
        ctx.stroke();
      }
      ctx.restore();
      // Точки (менее яркие)
      for (let i = 0; i < POINTS; i++) {
        const p = points.current[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, 2 * Math.PI);
        ctx.fillStyle = isDark ? "rgba(126,207,255,0.22)" : "rgba(0,0,0,0.18)";
        ctx.shadowColor = "transparent";
        ctx.fill();
      }
      // Пакеты
      for (let i = 0; i < packets.current.length; i++) {
        const pkt = packets.current[i];
        pkt.t += pkt.speed * pkt.dir;
        if (pkt.t < 0 || pkt.t > 1) {
          // Респавн на другой линии
          const line = lines.current[Math.floor(random(0, lines.current.length))];
          const dir = Math.random() > 0.5 ? 1 : -1;
          pkt.a = line.a;
          pkt.b = line.b;
          pkt.t = dir === 1 ? 0 : 1;
          pkt.dir = dir;
          pkt.speed = random(0.004, 0.009);
        }
        const x = lerp(pkt.a.x, pkt.b.x, pkt.t);
        const y = lerp(pkt.a.y, pkt.b.y, pkt.t);
        ctx.beginPath();
        ctx.arc(x, y, 2.2, 0, 2 * Math.PI);
        ctx.fillStyle = PACKET_COLOR;
        ctx.shadowColor = PACKET_SHADOW;
        ctx.shadowBlur = isDark ? 4 : 2;
        ctx.globalAlpha = isDark ? 0.7 : 0.5;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
      // Движение точек
      for (let i = 0; i < POINTS; i++) {
        const p = points.current[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }
      animationRef.current = requestAnimationFrame(animate);
    }
    animate();
    // Resize
    const handleResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
      updateLines();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'transparent',
        transition: 'opacity 0.5s'
      }}
    />
  );
};

export default NetworkBackground; 